/**
 * Colombian billing-recipient document validation (MID-555).
 *
 * FlotaOspina (TM Solutions) emits the DIAN invoice itself; the funnel only
 * captures who the invoice is for and sends it to the backend. The buyer picks a
 * recipient — themselves (`self`), a third-party company (`organization`, by
 * NIT) or a third-party person (`person`, by cédula) — and this module validates
 * the document for the chosen recipient. It replaces the Peru RUC (11-digit)
 * rule used by the existing checkout taxpayer input, which does not fit Colombia.
 *
 * NIT format: as confirmed with TM Solutions, the NIT is captured and sent
 * WITHOUT its verification digit (DV). The base rule mirrors the Fospina
 * whitelabel `tributaryNumber` regex (`^[1-9]\d{6,9}$`); a `-DV` suffix is
 * rejected so the DV can never reach the provider payload.
 */

/**
 * Billing recipient types the buyer can choose at checkout.
 * @type {{ SELF: 'self', ORGANIZATION: 'organization', PERSON: 'person' }}
 */
export const BILLING_RECIPIENT_TYPES = {
  SELF: 'self',
  ORGANIZATION: 'organization',
  PERSON: 'person',
};

// Base NIT: 7–10 digits, no leading zero (mirrors the Fospina whitelabel
// `tributaryNumber` regex). The `-DV` verification digit is NOT accepted: TM
// Solutions expects the base NIT only, so a DV suffix is rejected at the source.
const NIT_REGEX = /^[1-9]\d{6,9}$/;

// Colombian cédula de ciudadanía: 6–10 digits, no leading zero.
const CEDULA_REGEX = /^[1-9]\d{5,9}$/;

/**
 * Coerce a value to a trimmed string, treating null/undefined as empty.
 * @param {*} value - The value to normalize.
 * @returns {string} The trimmed string representation.
 */
const normalize = (value) => (value == null ? '' : String(value).trim());

/**
 * Whether a billing recipient is a third party — the invoice goes to the buyer
 * (comprador), not the traveler. True for `organization` and `person`; false for
 * `self` and any unset/unknown value. Single source of truth for the predicate
 * the UI gating and the purchase payload both branch on.
 * @param {string} [recipientType] - One of {@link BILLING_RECIPIENT_TYPES}.
 * @returns {boolean} True for a third-party recipient.
 */
export const isThirdPartyRecipient = (recipientType) =>
  recipientType === BILLING_RECIPIENT_TYPES.ORGANIZATION ||
  recipientType === BILLING_RECIPIENT_TYPES.PERSON;

/**
 * Whether the value is a valid Colombian NIT (company tax id).
 * @param {string} value - The NIT to validate.
 * @returns {boolean} True when the NIT matches the expected format.
 */
export const isValidNit = (value) => NIT_REGEX.test(normalize(value));

/**
 * Whether the value is a valid Colombian cédula (person id).
 * @param {string} value - The cédula to validate.
 * @returns {boolean} True when the cédula matches the expected format.
 */
export const isValidCedula = (value) => CEDULA_REGEX.test(normalize(value));

/**
 * Whether the billing document is valid for the chosen recipient type.
 *
 * - `self`: no document required (the traveler is invoiced), always valid.
 * - `organization`: requires a valid NIT.
 * - `person`: requires a valid cédula.
 * - anything else (no invoice / unset): valid (nothing to validate).
 *
 * @param {object} params - Parameters.
 * @param {string} [params.recipientType] - One of {@link BILLING_RECIPIENT_TYPES}.
 * @param {string} [params.document] - The taxpayer document (NIT or cédula).
 * @returns {boolean} True when the document is acceptable for the recipient type.
 */
export const isValidBillingDocument = ({ recipientType, document } = {}) => {
  switch (recipientType) {
    case BILLING_RECIPIENT_TYPES.ORGANIZATION:
      return isValidNit(document);
    case BILLING_RECIPIENT_TYPES.PERSON:
      return isValidCedula(document);
    default:
      return true;
  }
};
